import { useMutation } from "@apollo/client/react";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { normalizeTemplateTitle } from "@src/utils";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";

import TemplateListItem from "./TemplateListItem";
import { createNewTemplate } from "./utils";
import { CREATE_AUTO_TEMPLATE } from "../../graphql/mutations/AutoTemplates";
import { usePremiumFeature } from "../../hooks";

const AutoTemplatesList = ({
  title,
  subtitle,
  templates,
  templateType,
  onTemplateClick,
  onFavouriteToggle,
  dataTestId,
  isModal = false,
  hideAddNewButton = false,
  refetch,
}) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const { PremiumFeatureGate, UserPermissions } = usePremiumFeature();

  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);

  const sortedTemplates = [...templates].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  );

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      templateType,
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  if (
    (isModal && sortedTemplates.length === 0) ||
    (templateType === "FAVOURITES" && sortedTemplates.length === 0)
  ) {
    return null;
  }

  return (
    <div data-testid={dataTestId}>
      {title && (
        <div className="mb-sm">
          <h3 className="text-xl font-semibold">{title}</h3>
          {subtitle && (
            <p className="text-xs font-normal text-secondary">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="grid gap-md rounded-large"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill, minmax(250px, 1fr))",
          }}
        >
          {sortedTemplates.length > 0 &&
            sortedTemplates.map((template) => {
              const templateItem = (
                <TemplateListItem
                  dataTestId={`template-item-${normalizeTemplateTitle(
                    template.name,
                  )}`}
                  key={template.uuid || template.value}
                  icon={
                    template.templateType === "SOAP"
                      ? faSoapNote
                      : faFile
                  }
                  template={template}
                  onClick={() => onTemplateClick(template)}
                  onFavouriteToggle={
                    onFavouriteToggle
                      ? () => onFavouriteToggle(template)
                      : undefined
                  }
                  refetch={refetch}
                />
              );

              // If it's not a SCRIBENOTE_DEFAULT template, wrap it with PremiumFeatureGate
              if (template.templateType !== "SCRIBENOTE_DEFAULT") {
                return (
                  <PremiumFeatureGate
                    key={template.uuid || template.value}
                    featureName="Adaptive Templates"
                    iconPosition="top-right"
                    iconSize="sm"
                    className="block! w-full! relative!"
                    style={{
                      width: "100%",
                      position: "relative",
                      display: "block",
                    }}
                    requiredPermission={
                      UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
                    }
                  >
                    {templateItem}
                  </PremiumFeatureGate>
                );
              }

              return templateItem;
            })}

          {!isModal && !hideAddNewButton && (
            <div className="relative flex">
              <PremiumFeatureGate
                featureName={`New ${
                  templateType.charAt(0) +
                  templateType.slice(1).toLowerCase()
                } Template`}
                iconPosition="top-right"
                iconSize="sm"
                requiredPermission={
                  UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
                }
                className="button-premium-gate w-full"
              >
                <button
                  className="w-full h-full flex flex-col items-center justify-center flex-none border-2 border-dashed border-secondary rounded-standard p-md text-secondary hover:bg-secondary transition"
                  onClick={handleCreateNewTemplate}
                >
                  <FontAwesomeIcon icon={faPlus} size="lg" />
                </button>
              </PremiumFeatureGate>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoTemplatesList;
